import { CollectionViewer } from '@angular/cdk/collections';
import { Paginated } from '@next/core-lib/models';
import { NotificationActions } from '@next/core-lib/notification';
import { FilterableDataSource, AgrDataSource, PaginatedDataSource, PaginationModel } from '@next/core-lib/table-v2';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, shareReplay, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { PageAndSize, ReturnableInvoiceLine } from '../models/returnable-invoiceline.model';
import { ReturnableInvoiceService } from '../services/returnable-invoice.service';

export class ReturnableProductsDataSource
  extends AgrDataSource<ReturnableInvoiceLine>
  implements PaginatedDataSource<ReturnableInvoiceLine>, FilterableDataSource<string>
{
  private readonly _destroyPaginatorSubject = new Subject<void>();
  private readonly _destroySubject = new Subject<void>();
  private readonly _pageAndSizeSubject = new BehaviorSubject<PageAndSize>({
    pageIndex: 0,
    pageSize: 25,
  });
  private readonly _searchTermSubject = new BehaviorSubject<string>('');
  private readonly _externalInvoiceIdSubject = new BehaviorSubject<string>(undefined);
  private readonly _results$: Observable<readonly ReturnableInvoiceLine[]>;
  private readonly _data$: Observable<Paginated<ReturnableInvoiceLine>>;

  constructor(
    private _returnableInvoiceApiService: ReturnableInvoiceService,
    private _store: Store,
  ) {
    super();

    this._data$ = combineLatest([this._pageAndSizeSubject, this._searchTermSubject, this._externalInvoiceIdSubject]).pipe(
      takeUntil(this._destroySubject),
      // debounceTime prevents sending unnecessary requests to backend when interacting with the table
      // e.g.: filtering/pagination actions when not on the first table page
      debounceTime(10),
      tap((_) => this.loadingSubject.next(true)),
      switchMap(([pagination, searchTerm, externalInvoiceId]) =>
        this._returnableInvoiceApiService.getReturnableInvoiceLinesPaginated(pagination, externalInvoiceId, searchTerm),
      ),
      tap((_) => this.loadingSubject.next(false)),
      catchError((error) => {
        this._store.dispatch(NotificationActions.errorSnackbar({ message: error.message }));
        this.loadingSubject.next(false);
        return of({
          metadata: { currentPage: 0, pageSize: 0, totalPages: 0, totalResultCount: 0 },
          results: [],
        });
      }),
      shareReplay(1),
    );

    this._results$ = this._data$.pipe(
      map((x) => x?.results),
      filter<ReturnableInvoiceLine[]>(Boolean),
      startWith([]),
      shareReplay(1),
    );
  }

  setExternalInvoiceId(invoiceId: string) {
    this._externalInvoiceIdSubject.next(invoiceId);
    this.resetPagination();
  }

  applyFilters(searchTerm: string) {
    this._searchTermSubject.next(searchTerm);
    this.resetPagination();
  }

  applyPage(pageIndex: number, pageSize: number): void {
    this._pageAndSizeSubject.next({ pageIndex, pageSize });
  }

  connect(): Observable<readonly ReturnableInvoiceLine[]> {
    return this._results$;
  }

  disconnect(_: CollectionViewer): void {
    super.disconnect(_);
    if (!this._destroySubject.isStopped) {
      this._destroySubject.next();
      this._destroySubject.complete();
    }
  }

  connectPaginator(): Observable<PaginationModel> {
    return combineLatest([this._data$, this._pageAndSizeSubject]).pipe(
      takeUntil(this._destroyPaginatorSubject),
      map(([data, { pageIndex, pageSize }]) => ({
        pageSize,
        pageIndex,
        length: data.metadata.totalResultCount,
      })),
    );
  }

  disconnectPaginator(): void {
    if (!this._destroyPaginatorSubject.isStopped) {
      this._destroyPaginatorSubject.next();
      this._destroyPaginatorSubject.complete();
    }
  }

  private resetPagination(): void {
    this._pageAndSizeSubject.pipe(take(1)).subscribe(({ pageSize }) => this._pageAndSizeSubject.next({ pageIndex: 0, pageSize }));
  }
}
